import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useProfile } from '../../context/ProfileContext';
import { useTheme } from '../../context/ThemeContext';
import { supabase } from '../../lib/supabase';

// Icons
const ArrowLeftIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 12H5" />
    <path d="M12 19l-7-7 7-7" />
  </svg>
);

const StarIcon = ({ filled = false, size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? "#ffd700" : "none"} stroke="#ffd700" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

const MapPinIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const ShieldIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const CheckIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const InfoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

// Star Rating Component
const StarRating = ({ label, value, onChange, theme }) => {
  const [hover, setHover] = useState(0);

  return (
    <div style={styles.ratingRow}>
      <span style={{ ...styles.ratingLabel, color: theme.text }}>{label}</span>
      <div style={styles.starsContainer}>
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            style={styles.starButton}
            onMouseEnter={() => setHover(star)}
            onMouseLeave={() => setHover(0)}
            onClick={() => onChange(star)}
          >
            <StarIcon filled={star <= (hover || value)} />
          </button>
        ))}
      </div>
    </div>
  );
};

// Yes/No Toggle Component
const YesNoToggle = ({ label, value, onChange, theme, isDark }) => (
  <div style={styles.toggleRow}>
    <span style={{ ...styles.toggleLabel, color: theme.text }}>{label}</span>
    <div style={styles.toggleButtons}>
      <button
        type="button"
        style={{
          ...styles.toggleButton,
          backgroundColor: value === true ? theme.accent : 'transparent',
          borderColor: value === true ? theme.accent : theme.border,
          color: value === true ? (isDark ? '#000' : '#fff') : theme.textMuted,
        }}
        onClick={() => onChange(true)}
      >
        Yes
      </button>
      <button
        type="button"
        style={{
          ...styles.toggleButton,
          backgroundColor: value === false ? theme.accent : 'transparent',
          borderColor: value === false ? theme.accent : theme.border,
          color: value === false ? (isDark ? '#000' : '#fff') : theme.textMuted,
        }}
        onClick={() => onChange(false)}
      >
        No
      </button>
    </div>
  </div>
);

// Three-option Toggle Component (Yes/No/Not Needed)
const ThreeOptionToggle = ({ label, value, onChange, theme, isDark }) => (
  <div style={styles.toggleRow}>
    <span style={{ ...styles.toggleLabel, color: theme.text }}>{label}</span>
    <div style={styles.toggleButtons}>
      <button
        type="button"
        style={{
          ...styles.toggleButton,
          backgroundColor: value === 'yes' ? theme.accent : 'transparent',
          borderColor: value === 'yes' ? theme.accent : theme.border,
          color: value === 'yes' ? (isDark ? '#000' : '#fff') : theme.textMuted,
        }}
        onClick={() => onChange('yes')}
      >
        Yes
      </button>
      <button
        type="button"
        style={{
          ...styles.toggleButton,
          backgroundColor: value === 'no' ? theme.accent : 'transparent',
          borderColor: value === 'no' ? theme.accent : theme.border,
          color: value === 'no' ? (isDark ? '#000' : '#fff') : theme.textMuted,
        }}
        onClick={() => onChange('no')}
      >
        No
      </button>
      <button
        type="button"
        style={{
          ...styles.toggleButton,
          backgroundColor: value === 'not_needed' ? theme.accent : 'transparent',
          borderColor: value === 'not_needed' ? theme.accent : theme.border,
          color: value === 'not_needed' ? (isDark ? '#000' : '#fff') : theme.textMuted,
        }}
        onClick={() => onChange('not_needed')}
      >
        Wasn't needed
      </button>
    </div>
  </div>
);

const TradeReview = () => {
  const { theme, isDark } = useTheme();
  const navigate = useNavigate();
  const { id: tradeId } = useParams();
  const { activeProfile } = useProfile();

  // Loading/Error/Success states
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Trade data
  const [tradeData, setTradeData] = useState(null);
  const [partnerData, setPartnerData] = useState(null);
  const [locationData, setLocationData] = useState(null);
  const [scheduleId, setScheduleId] = useState(null);
  const [requestedCards, setRequestedCards] = useState([]);

  // Partner showed up?
  const [partnerShowed, setPartnerShowed] = useState(null);

  // Trade Outcome
  const [tradeCompleted, setTradeCompleted] = useState(null);
  const [boughtCards, setBoughtCards] = useState(null);

  // Trader Ratings
  const [communicationRating, setCommunicationRating] = useState(0);
  const [fairnessRating, setFairnessRating] = useState(0);
  const [timelinessRating, setTimelinessRating] = useState(0);
  const [overallRating, setOverallRating] = useState(0);

  // Scalper/Vendor Detection (Private)
  const [seemedPushySeller, setSeeemedPushySeller] = useState(null);
  const [pushedOwnContent, setPushedOwnContent] = useState(null);

  // Location Review
  const [cleanlinessRating, setCleanlinessRating] = useState(0);
  const [friendlinessRating, setFriendlinessRating] = useState(0);
  const [spaceRating, setSpaceRating] = useState(0);
  const [staffHelped, setStaffHelped] = useState(null); // 'yes', 'no', 'not_needed', or null
  const [staffRating, setStaffRating] = useState(0);

  // Private Notes
  const [privateNotes, setPrivateNotes] = useState('');
  const [locationNotes, setLocationNotes] = useState('');

  // Fetch trade details
  useEffect(() => {
    const fetchTradeDetails = async () => {
      if (!tradeId || !activeProfile?.id) {
        setLoading(false);
        return;
      }

      try {
        // Fetch trade request with schedule, location, and card
        const { data: trade, error: tradeError } = await supabase
          .from('trade_requests')
          .select(`
            id,
            requester_id,
            card_owner_id,
            card_id,
            status,
            cards (
              id,
              name,
              digital_image_url
            ),
            trade_schedules (
              id,
              selected_date,
              selected_time,
              location_id,
              locations (
                id,
                store_name,
                address,
                city,
                state
              )
            )
          `)
          .eq('id', tradeId)
          .single();

        if (tradeError) throw tradeError;

        if (!trade) {
          setError('Trade not found');
          setLoading(false);
          return;
        }

        // Get schedule (could be array or object)
        const schedule = Array.isArray(trade.trade_schedules)
          ? trade.trade_schedules[0]
          : trade.trade_schedules;

        if (!schedule) {
          setError('No schedule found for this trade');
          setLoading(false);
          return;
        }

        setTradeData(trade);
        setScheduleId(schedule.id);
        setLocationData(schedule.locations);

        // Get the initially requested card
        const cards = [];
        if (trade.cards) {
          cards.push({
            id: trade.cards.id,
            name: trade.cards.name,
            image: trade.cards.digital_image_url,
          });
        }

        // Also fetch any additional cards from trade_interests for this trade
        const { data: interests } = await supabase
          .from('trade_interests')
          .select(`
            card_id,
            cards (
              id,
              name,
              digital_image_url
            )
          `)
          .eq('trade_request_id', tradeId)
          .eq('is_initiating_card', true);

        if (interests) {
          interests.forEach((interest) => {
            if (interest.cards && !cards.find(c => c.id === interest.cards.id)) {
              cards.push({
                id: interest.cards.id,
                name: interest.cards.name,
                image: interest.cards.digital_image_url,
              });
            }
          });
        }

        setRequestedCards(cards);

        // Determine trading partner
        const isRequester = trade.requester_id === activeProfile.id;
        const partnerId = isRequester ? trade.card_owner_id : trade.requester_id;

        // Fetch partner info
        const { data: partner, error: partnerError } = await supabase
          .from('users')
          .select('id, username, avatar_url')
          .eq('id', partnerId)
          .single();

        if (!partnerError && partner) {
          setPartnerData(partner);
        }

        // Check if user already reviewed this trade
        const { data: existingReview } = await supabase
          .from('trade_reviews')
          .select('id')
          .eq('trade_schedule_id', schedule.id)
          .eq('reviewer_id', activeProfile.id)
          .maybeSingle();

        if (existingReview) {
          setError('You have already reviewed this trade');
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching trade:', err);
        setError('Failed to load trade details');
        setLoading(false);
      }
    };

    fetchTradeDetails();
  }, [tradeId, activeProfile?.id]);

  // Submit review
  const handleSubmit = async () => {
    if (!activeProfile?.id || !scheduleId || !partnerData?.id) {
      setError('Missing required data');
      return;
    }

    // Validate based on flow path
    if (partnerShowed === null) {
      setError('Please indicate if your trading partner showed up');
      return;
    }

    if (partnerShowed) {
      if (tradeCompleted === null) {
        setError('Please indicate if the meetup resulted in a trade');
        return;
      }

      if (tradeCompleted) {
        // Full trade - require everything
        if (boughtCards === null) {
          setError('Please indicate if you bought cards from the trader');
          return;
        }
        if (!communicationRating || !fairnessRating || !timelinessRating || !overallRating) {
          setError('Please complete all trader ratings');
          return;
        }
        if (!cleanlinessRating || !friendlinessRating || !spaceRating) {
          setError('Please complete all location ratings');
          return;
        }
        if (staffHelped === 'yes' && !staffRating) {
          setError('Please rate the staff');
          return;
        }
      }
    }

    setSubmitting(true);
    setError(null);

    try {
      // Build review data conditionally
      const reviewData = {
        trade_schedule_id: scheduleId,
        reviewer_id: activeProfile.id,
        reviewed_user_id: partnerData.id,
        partner_showed: partnerShowed,
        trade_completed: partnerShowed ? tradeCompleted : false,
        bought_cards: partnerShowed ? boughtCards : false,
        communication_rating: partnerShowed ? (communicationRating || null) : null,
        fairness_rating: (partnerShowed && tradeCompleted) ? (fairnessRating || null) : null,
        timeliness_rating: (partnerShowed && tradeCompleted) ? (timelinessRating || null) : null,
        overall_rating: partnerShowed ? (overallRating || null) : null,
        seemed_pushy_seller: (partnerShowed && tradeCompleted) ? seemedPushySeller === true : false,
        pushed_own_content: (partnerShowed && tradeCompleted) ? pushedOwnContent === true : false,
        private_notes: privateNotes || null,
      };

      const { error: reviewError } = await supabase
        .from('trade_reviews')
        .insert(reviewData);

      if (reviewError) throw reviewError;

      // Insert location review only when partner showed and trade completed
      if (partnerShowed && tradeCompleted && locationData) {
        const { error: locationError } = await supabase
          .from('location_ratings')
          .insert({
            trade_schedule_id: scheduleId,
            reviewer_id: activeProfile.id,
            location_id: locationData.id,
            cleanliness_rating: cleanlinessRating,
            friendliness_rating: friendlinessRating,
            space_rating: spaceRating,
            staff_helped: staffHelped === 'yes' ? true : staffHelped === 'no' ? false : null,
            staff_rating: staffHelped === 'yes' ? staffRating : null,
            private_notes: locationNotes || null,
          });

        if (locationError) throw locationError;
      }

      // Update trade_requests status to completed if not already
      if (tradeData?.status !== 'completed') {
        await supabase.from('trade_requests')
          .update({ status: 'completed' })
          .eq('id', tradeId);
      }

      // Update schedule review_status so modal query filters it out
      // Check if both users have now reviewed
      const { data: allReviews } = await supabase
        .from('trade_reviews')
        .select('id')
        .eq('trade_schedule_id', scheduleId);

      const newReviewStatus = (allReviews?.length || 0) >= 2 ? 'both_completed' : 'one_completed';
      await supabase.from('trade_schedules')
        .update({ review_status: newReviewStatus })
        .eq('id', scheduleId);

      // Mark as reviewed so PendingReviewModal skips it
      sessionStorage.setItem(`reviewed_${scheduleId}`, 'true');

      // Show success message
      setSubmitting(false);
      setSuccess(true);
    } catch (err) {
      console.error('Error submitting review:', err);
      setError('Failed to submit review. Please try again.');
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div style={{ ...styles.container, backgroundColor: theme.bg }}>
        <div style={styles.loadingContainer}>
          <div style={{ ...styles.spinner, borderTopColor: theme.accent, borderColor: theme.border }} />
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div style={{ ...styles.container, backgroundColor: theme.bg }}>
        <div style={styles.successContainer}>
          <div style={{ ...styles.successIcon, backgroundColor: `${theme.success}20`, color: theme.success }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <h2 style={{ ...styles.successTitle, color: theme.text }}>Thanks for your review!</h2>
          <p style={{ ...styles.successText, color: theme.textMuted }}>
            Your feedback helps CardChase grow and keeps the trading community strong.
          </p>
          <button
            style={{
              marginTop: 24,
              padding: '14px 32px',
              borderRadius: 12,
              border: 'none',
              backgroundColor: theme.accent,
              color: '#fff',
              fontSize: 16,
              fontWeight: 600,
              cursor: 'pointer',
            }}
            onClick={() => navigate('/home')}
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  if (error && !tradeData) {
    return (
      <div style={{ ...styles.container, backgroundColor: theme.bg }}>
        <div style={{ ...styles.header, backgroundColor: theme.bg, borderColor: theme.border }}>
          <button style={{ ...styles.backButton, color: theme.accent }} onClick={handleBack}>
            <ArrowLeftIcon />
          </button>
          <h1 style={{ ...styles.headerTitle, color: theme.text }}>Review Trade</h1>
        </div>
        <div style={styles.errorContainer}>
          <span style={{ ...styles.errorText, color: theme.error }}>{error}</span>
          <button
            style={{ ...styles.backToTradesButton, backgroundColor: theme.accent, color: isDark ? '#000' : '#fff' }}
            onClick={() => navigate('/meetup/trades')}
          >
            Back to Trades
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...styles.container, backgroundColor: theme.bg }}>
      {/* Header */}
      <div style={{ ...styles.header, backgroundColor: theme.bg, borderColor: theme.border }}>
        <button style={{ ...styles.backButton, color: theme.accent }} onClick={handleBack}>
          <ArrowLeftIcon />
        </button>
        <div style={styles.headerContent}>
          <h1 style={{ ...styles.headerTitle, color: theme.text }}>How was your trade?</h1>
          <span style={{ ...styles.headerSubtitle, color: theme.textMuted }}>
            Your feedback helps build trust
          </span>
        </div>
      </div>

      {/* Scrollable Content */}
      <div style={styles.content}>
        {/* Error Alert */}
        {error && (
          <div style={{ ...styles.errorAlert, backgroundColor: `${theme.error}20`, borderColor: theme.error }}>
            <span style={{ color: theme.error }}>{error}</span>
          </div>
        )}

        {/* Cards Requested Section */}
        {requestedCards.length > 0 && (
          <div style={{ ...styles.cardsSection, backgroundColor: theme.bgCard, borderColor: theme.border }}>
            <span style={{ ...styles.cardsSectionLabel, color: theme.textMuted }}>
              Cards requested for this trade
            </span>
            <div style={styles.cardsRow}>
              {requestedCards.map((card, index) => (
                <div key={card.id || index} style={styles.cardItem}>
                  <img
                    src={card.image}
                    alt={card.name}
                    style={styles.cardImage}
                  />
                  <span style={{ ...styles.cardName, color: theme.text }}>{card.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Section 1: Trade Outcome & Trader Review */}
        <div style={{ ...styles.section, backgroundColor: theme.bgCard, borderColor: theme.border }}>
          <div style={styles.sectionHeader}>
            <div style={styles.partnerInfo}>
              {partnerData?.avatar_url ? (
                <img src={partnerData.avatar_url} alt="" style={styles.avatar} />
              ) : (
                <div style={{ ...styles.avatarPlaceholder, backgroundColor: theme.accent }}>
                  <span style={{ color: isDark ? '#000' : '#fff', fontWeight: 700 }}>
                    {partnerData?.username?.[0]?.toUpperCase() || '?'}
                  </span>
                </div>
              )}
              <div>
                <h2 style={{ ...styles.sectionTitle, color: theme.text }}>
                  Rate @{partnerData?.username || 'Trader'}
                </h2>
                <span style={{ ...styles.sectionSubtitle, color: theme.textMuted }}>
                  How was your trading experience?
                </span>
              </div>
            </div>
          </div>

          {/* Q1: Did partner show up? */}
          <div style={styles.outcomeGroup}>
            <YesNoToggle
              label="Did your trading partner show up?"
              value={partnerShowed}
              onChange={(val) => {
                setPartnerShowed(val);
                if (!val) {
                  setTradeCompleted(null);
                  setBoughtCards(null);
                }
              }}
              theme={theme}
              isDark={isDark}
            />
          </div>

          {/* No-show path */}
          {partnerShowed === false && (
            <div style={{ ...styles.noShowMessage, backgroundColor: `${theme.error}10`, borderColor: `${theme.error}30` }}>
              <span style={{ ...styles.noShowText, color: theme.textMuted }}>
                Sorry to hear that. You can add optional notes below.
              </span>
            </div>
          )}

          {/* Q2: Did meetup result in a trade? (only if partner showed) */}
          {partnerShowed && (
            <div style={styles.outcomeGroup}>
              <YesNoToggle
                label="Did your meetup result in a trade?"
                value={tradeCompleted}
                onChange={setTradeCompleted}
                theme={theme}
                isDark={isDark}
              />
              {tradeCompleted && (
                <YesNoToggle
                  label="Did you buy cards from the trader?"
                  value={boughtCards}
                  onChange={setBoughtCards}
                  theme={theme}
                  isDark={isDark}
                />
              )}
            </div>
          )}

          {/* Star Ratings - show when partner showed */}
          {partnerShowed && tradeCompleted !== null && (
            <div style={{ ...styles.ratingsGroup, borderTop: `1px solid ${theme.border}`, paddingTop: 16, marginTop: 16 }}>
              {/* Always show communication + overall when partner showed */}
              <StarRating
                label="Communication"
                value={communicationRating}
                onChange={setCommunicationRating}
                theme={theme}
              />
              {/* Fairness + Timeliness only when trade completed */}
              {tradeCompleted && (
                <>
                  <StarRating
                    label="Fairness"
                    value={fairnessRating}
                    onChange={setFairnessRating}
                    theme={theme}
                  />
                  <StarRating
                    label="Timeliness"
                    value={timelinessRating}
                    onChange={setTimelinessRating}
                    theme={theme}
                  />
                </>
              )}
              <StarRating
                label="Overall"
                value={overallRating}
                onChange={setOverallRating}
                theme={theme}
              />
            </div>
          )}
        </div>

        {/* Section 2: Help CardChase (Private) - only when partner showed and trade completed */}
        {partnerShowed && tradeCompleted && (
          <div style={{ ...styles.section, backgroundColor: theme.bgCard, borderColor: theme.border }}>
            <div style={styles.sectionHeader}>
              <div style={{ ...styles.shieldBadge, backgroundColor: `${theme.accent}20` }}>
                <ShieldIcon />
              </div>
              <div>
                <h2 style={{ ...styles.sectionTitle, color: theme.text }}>Help CardChase</h2>
                <span style={{ ...styles.sectionSubtitle, color: theme.textMuted }}>
                  This is private and won't be shared
                </span>
              </div>
            </div>

            <div style={{ ...styles.infoBox, backgroundColor: isDark ? 'rgba(14, 165, 233, 0.1)' : 'rgba(14, 165, 233, 0.08)', borderColor: isDark ? 'rgba(14, 165, 233, 0.2)' : 'rgba(14, 165, 233, 0.15)' }}>
              <div style={{ ...styles.infoIcon, color: theme.accent }}>
                <InfoIcon />
              </div>
              <span style={{ ...styles.infoText, color: theme.textSecondary }}>
                CardChase is committed to finding vendors and scalpers who are only interested in selling cards, not fair trades. It's fine for traders to mention their IG or other business - we're just looking for pushy behavior.
              </span>
            </div>

            <div style={styles.toggleGroup}>
              <YesNoToggle
                label="Did this trader seem more interested in pushing cards to sell than fair trading?"
                value={seemedPushySeller}
                onChange={setSeeemedPushySeller}
                theme={theme}
                isDark={isDark}
              />
              <YesNoToggle
                label="Did they push their own content, shows, or events?"
                value={pushedOwnContent}
                onChange={setPushedOwnContent}
                theme={theme}
                isDark={isDark}
              />
            </div>
          </div>
        )}

        {/* Private Notes - always available */}
        {partnerShowed !== null && (
          <div style={{ ...styles.section, backgroundColor: theme.bgCard, borderColor: theme.border }}>
            <div style={styles.noteBox}>
              <span style={{ ...styles.noteLabel, color: theme.text }}>
                {partnerShowed ? 'Anything else we should know about the trader?' : 'Any additional details about the no-show?'}
              </span>
              <textarea
                style={{
                  ...styles.textArea,
                  backgroundColor: theme.bg,
                  borderColor: theme.border,
                  color: theme.text,
                }}
                placeholder="Optional notes..."
                value={privateNotes}
                onChange={(e) => setPrivateNotes(e.target.value)}
                rows={3}
              />
            </div>
          </div>
        )}

        {/* Section 3: Location Review - only when partner showed and trade completed */}
        {partnerShowed && tradeCompleted && locationData && (
          <div style={{ ...styles.section, backgroundColor: theme.bgCard, borderColor: theme.border }}>
            <div style={styles.sectionHeader}>
              <div style={{ ...styles.locationBadge, backgroundColor: `${theme.success}20`, color: theme.success }}>
                <MapPinIcon />
              </div>
              <div>
                <h2 style={{ ...styles.sectionTitle, color: theme.text }}>
                  {locationData.store_name || 'Rate Location'}
                </h2>
                <span style={{ ...styles.sectionSubtitle, color: theme.textMuted }}>
                  {locationData.address}, {locationData.city}
                </span>
              </div>
            </div>

            <div style={styles.ratingsGroup}>
              <StarRating
                label="How clean was the meetup spot?"
                value={cleanlinessRating}
                onChange={setCleanlinessRating}
                theme={theme}
              />
              <StarRating
                label="How friendly was the environment?"
                value={friendlinessRating}
                onChange={setFriendlinessRating}
                theme={theme}
              />
              <StarRating
                label="Enough space to trade comfortably?"
                value={spaceRating}
                onChange={setSpaceRating}
                theme={theme}
              />
            </div>

            {/* Staff Section */}
            <div style={{ ...styles.staffSection, borderColor: theme.border }}>
              <ThreeOptionToggle
                label="If needed, did staff help with the trade or your experience?"
                value={staffHelped}
                onChange={setStaffHelped}
                theme={theme}
                isDark={isDark}
              />
              {staffHelped === 'yes' && (
                <StarRating
                  label="How was the staff?"
                  value={staffRating}
                  onChange={setStaffRating}
                  theme={theme}
                />
              )}
            </div>

            <div style={styles.noteBox}>
              <span style={{ ...styles.noteLabel, color: theme.text }}>
                Anything else we should know about this location?
              </span>
              <textarea
                style={{
                  ...styles.textArea,
                  backgroundColor: theme.bg,
                  borderColor: theme.border,
                  color: theme.text,
                }}
                placeholder="Optional notes..."
                value={locationNotes}
                onChange={(e) => setLocationNotes(e.target.value)}
                rows={2}
              />
            </div>
          </div>
        )}

        {/* Submit Button */}
        <button
          style={{
            ...styles.submitButton,
            backgroundColor: submitting ? theme.border : theme.accent,
            color: isDark ? '#000' : '#fff',
            opacity: submitting ? 0.7 : 1,
          }}
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? (
            <div style={{ ...styles.buttonSpinner, borderTopColor: isDark ? '#000' : '#fff' }} />
          ) : (
            <>
              <CheckIcon />
              <span>Submit Review</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: '100vh',
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
  },
  spinner: {
    width: 32,
    height: 32,
    border: '3px solid',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    gap: 16,
    padding: 20,
  },
  errorText: {
    fontSize: 14,
    textAlign: 'center',
  },
  successContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    gap: 16,
    padding: 40,
  },
  successIcon: {
    width: 64,
    height: 64,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  successTitle: {
    fontSize: 22,
    fontWeight: 700,
    margin: 0,
    textAlign: 'center',
  },
  successText: {
    fontSize: 14,
    textAlign: 'center',
    lineHeight: 1.5,
    maxWidth: 280,
  },
  backToTradesButton: {
    padding: '12px 24px',
    borderRadius: 8,
    border: 'none',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    padding: '16px 20px',
    gap: 12,
    borderBottom: '1px solid',
  },
  backButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerContent: {
    flex: 1,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 700,
    margin: 0,
  },
  headerSubtitle: {
    fontSize: 12,
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: '20px 20px 100px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  errorAlert: {
    padding: '12px 16px',
    borderRadius: 8,
    border: '1px solid',
    fontSize: 13,
  },
  // Cards Section
  cardsSection: {
    borderRadius: 12,
    border: '1px solid',
    padding: 16,
  },
  cardsSectionLabel: {
    fontSize: 12,
    fontWeight: 500,
    marginBottom: 12,
    display: 'block',
  },
  cardsRow: {
    display: 'flex',
    gap: 12,
    overflowX: 'auto',
    paddingBottom: 4,
  },
  cardItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
    flexShrink: 0,
  },
  cardImage: {
    width: 80,
    height: 112,
    borderRadius: 6,
    objectFit: 'cover',
    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
  },
  cardName: {
    fontSize: 11,
    fontWeight: 500,
    textAlign: 'center',
    maxWidth: 80,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // Main sections
  section: {
    borderRadius: 12,
    border: '1px solid',
    padding: 16,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
  },
  partnerInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  avatar: {
    width: 44,
    height: 44,
    borderRadius: '50%',
    objectFit: 'cover',
  },
  avatarPlaceholder: {
    width: 44,
    height: 44,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 18,
  },
  shieldBadge: {
    width: 44,
    height: 44,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  locationBadge: {
    width: 44,
    height: 44,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 600,
    margin: 0,
  },
  sectionSubtitle: {
    fontSize: 12,
  },
  outcomeGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  noShowMessage: {
    padding: 12,
    borderRadius: 8,
    border: '1px solid',
    marginTop: 8,
  },
  noShowText: {
    fontSize: 13,
    lineHeight: 1.5,
  },
  ratingsGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  ratingRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  ratingLabel: {
    fontSize: 13,
    fontWeight: 500,
  },
  starsContainer: {
    display: 'flex',
    gap: 4,
  },
  starButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'transform 0.15s ease',
  },
  infoBox: {
    display: 'flex',
    gap: 10,
    padding: 12,
    borderRadius: 8,
    border: '1px solid',
    marginBottom: 16,
  },
  infoIcon: {
    flexShrink: 0,
    marginTop: 2,
  },
  infoText: {
    fontSize: 12,
    lineHeight: 1.5,
  },
  toggleGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  toggleRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  toggleLabel: {
    fontSize: 13,
    fontWeight: 500,
    lineHeight: 1.4,
  },
  toggleButtons: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
  },
  toggleButton: {
    padding: '8px 16px',
    borderRadius: 6,
    border: '1px solid',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  noteBox: {
    marginTop: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  noteLabel: {
    fontSize: 13,
    fontWeight: 500,
  },
  textArea: {
    width: '100%',
    padding: 12,
    borderRadius: 8,
    border: '1px solid',
    fontSize: 14,
    resize: 'vertical',
    fontFamily: 'inherit',
    outline: 'none',
  },
  staffSection: {
    marginTop: 16,
    paddingTop: 16,
    borderTop: '1px solid',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  submitButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '16px 24px',
    borderRadius: 12,
    border: 'none',
    fontSize: 16,
    fontWeight: 700,
    cursor: 'pointer',
    marginTop: 8,
    transition: 'opacity 0.15s ease',
  },
  buttonSpinner: {
    width: 20,
    height: 20,
    border: '2px solid transparent',
    borderTopWidth: 2,
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
};

export default TradeReview;
